import { strapiService } from './strapiClient'
import { authApiClient } from './axios'
import type { OrgProfileAttributes } from '@/types/strapi'

interface UpdateUserAccountData {
  email?: string
  phone?: string
  username?: string
}

interface UpdateUserAccountResponse {
  id: number
  username: string
  email: string
  [key: string]: unknown
}

interface UpdateOrgProfileResponse {
  data: Record<string, unknown>
  meta: Record<string, unknown>
}

interface FileUploadResponse {
  data: Array<{
    id: number
    documentId: string
    name: string
    url: string
    [key: string]: unknown
  }>
}

interface AppSettings {
  id: number
  documentId: string
  maintenance_mode: boolean
  logo?: {
    url: string
    alternativeText?: string
    name: string
  }
  primary_color?: string
  secondary_color?: string
  allow_signup?: boolean
  booking_cancellation_days?: number
  disable_notifications?: boolean
  captcha?: boolean
  accessibility_widget?: boolean | null
  createdAt: string
  updatedAt: string
  publishedAt: string
}

interface AppSettingsResponse {
  data: any
  meta: Record<string, unknown>
}

/**
 * Service for managing organization settings and profile updates
 */
export const settingsService = {
  /**
   * Get application settings including maintenance mode
   */
  async getAppSettings(): Promise<AppSettings | null> {
    try {

      const response = await strapiService.find('setting') as AppSettingsResponse
      return response?.data !== null ? response.data : null
    } catch (error) {
      console.error('Error fetching app settings:', error)
      return null
    }
  },

  /**
   * Update organization profile data using documentId (Strapi v5)
   */
  async updateOrgProfile(documentId: string, data: Partial<OrgProfileAttributes>, locale?: string): Promise<UpdateOrgProfileResponse> {
    try {
      const response = await strapiService.update('org-profiles', documentId, data, { locale })
      return response as unknown as UpdateOrgProfileResponse
    } catch (error) {
      console.error('Error updating org profile:', error)
      throw error
    }
  },

  /**
   * Update user account information (email, phone, etc.)
   */
  async updateUserAccount(userId: number, data: UpdateUserAccountData): Promise<UpdateUserAccountResponse> {
    try {
      const response = await authApiClient.put(`/users/${userId}`, data)
      return response.data as UpdateUserAccountResponse
    } catch (error) {
      console.error('Error updating user account:', error)
      throw error
    }
  },

  /**
   * Upload profile image
   */
  async uploadProfileImage(file: File, entityId: number): Promise<FileUploadResponse> {
    try {
      const formData = new FormData()
      formData.append('files', file)
      formData.append('ref', 'api::org-profile.org-profile')
      formData.append('refId', entityId.toString())
      formData.append('field', 'logo')

      const response = await strapiService.files.upload(formData)
      return response as unknown as FileUploadResponse
    } catch (error) {
      console.error('Error uploading profile image:', error)
      throw error
    }
  },

  /**
   * Upload cover image
   */
  async uploadCoverImage(file: File, entityId: number): Promise<FileUploadResponse> {
    try {
      const formData = new FormData()
      formData.append('files', file)
      formData.append('ref', 'api::org-profile.org-profile')
      formData.append('refId', entityId.toString())
      formData.append('field', 'cover_image')

      const response = await strapiService.files.upload(formData)
      return response as unknown as FileUploadResponse
    } catch (error) {
      console.error('Error uploading cover image:', error)
      throw error
    }
  },

  /**
   * Upload documents to organization
   */
  async uploadDocuments(files: File[], entityId: number): Promise<FileUploadResponse> {
    try {
      const formData = new FormData()
      files.forEach(file => {
        formData.append('files', file)
      })
      formData.append('ref', 'api::org-profile.org-profile')
      formData.append('refId', entityId.toString())
      formData.append('field', 'registration_doc')

      const response = await strapiService.files.upload(formData)
      return response as unknown as FileUploadResponse
    } catch (error) {
      console.error('Error uploading documents:', error)
      throw error
    }
  },

  /**
   * Upload photos to photo library
   */
  async uploadPhotos(files: File[], entityId: number): Promise<FileUploadResponse> {
    try {
      const formData = new FormData()
      files.forEach(file => {
        formData.append('files', file)
      })
      formData.append('ref', 'api::org-profile.org-profile')
      formData.append('refId', entityId.toString())
      formData.append('field', 'org_photo_library')

      const response = await strapiService.files.upload(formData)
      return response as unknown as FileUploadResponse
    } catch (error) {
      console.error('Error uploading photos:', error)
      throw error
    }
  },

  /**
   * Update organization profile visibility using documentId (Strapi v5)
   */
  async updateProfileVisibility(documentId: string, isPublic: boolean): Promise<UpdateOrgProfileResponse> {
    try {
      const response = await strapiService.update('org-profiles', documentId, {
        organization_status: isPublic
      })
      return response as unknown as UpdateOrgProfileResponse
    } catch (error) {
      console.error('Error updating profile visibility:', error)
      throw error
    }
  },

  /**
   * Remove profile image by setting logo field to null
   */
  async removeProfileImage(entityId: number): Promise<UpdateOrgProfileResponse> {
    try {
      // For simplicity, we'll use the entity ID as document ID
      // In a production environment, you might need a more robust way to get the documentId
      const response = await strapiService.update('org-profiles', entityId.toString(), {
        logo: null
      })
      return response as unknown as UpdateOrgProfileResponse
    } catch (error) {
      console.error('Error removing profile image:', error)
      throw error
    }
  },

  /**
   * Remove cover image by setting cover_image field to null
   */
  async removeCoverImage(entityId: number): Promise<UpdateOrgProfileResponse> {
    try {
      // For simplicity, we'll use the entity ID as document ID
      // In a production environment, you might need a more robust way to get the documentId
      const response = await strapiService.update('org-profiles', entityId.toString(), {
        cover_image: null
      })
      return response as unknown as UpdateOrgProfileResponse
    } catch (error) {
      console.error('Error removing cover image:', error)
      throw error
    }
  },

  /**
   * Remove photo from library by updating the org_photo_library field
   * This is a simplified implementation that requires the frontend to manage the photo array
   */
  async removePhotoFromLibrary(entityId: number, photoIds: number[]): Promise<UpdateOrgProfileResponse> {
    try {
      // Update the org_photo_library field with the remaining photo IDs
      const response = await strapiService.update('org-profiles', entityId.toString(), {
        org_photo_library: photoIds
      })
      return response as unknown as UpdateOrgProfileResponse
    } catch (error) {
      console.error('Error removing photo from library:', error)
      throw error
    }
  }
}

export default settingsService